// components/members/MemberCard.js
import React from 'react';
import { Container, Image } from 'react-bootstrap';
import { getOrdinalSuffix, MEMBER_TYPES } from '../../utils/membersConfig';
import useMemberNavigation from '../../hooks/useMemberNavigation';
import './MemberCard.css';

const MemberCard = ({ 
  member, 
  memberType,
  variant = 'full', 
  onClick,
  containerId = 'feedContainerNormalView',
}) => {
  const { navigateToMemberFromRaw } = useMemberNavigation();

  if (!member) return null;

  // Detect data format based on available fields
  const isExecutiveFormat = member.full_name !== undefined;
  
  // Extract data based on format
  const fullName = isExecutiveFormat 
    ? member.full_name 
    : `${member.first_name} ${member.last_name}`;
  
  const imageSource = isExecutiveFormat 
    ? member.url 
    : member.depication?.imageUrl;

  // For executive/judicial format
  const presidentNumber = member.president_num;
  const termStart = member.term_start_date;
  const termEnd = member.term_end_date;
  const description = member.description;
  const title = member.title; // e.g., "Chief Justice"
  const appointmentDate = member.appointment_date; // e.g., "September 29, 2005"

  // For congress format
  const latestTerm = member.terms?.[member.terms.length - 1] || {};
  const { chamber, stateName, district, startYear } = latestTerm;

  // Determine member type if not provided
  const determinedType = memberType || (
    presidentNumber ? MEMBER_TYPES.PRESIDENT :
    description && !chamber ? MEMBER_TYPES.JUSTICE :
    chamber === 'Senate' ? MEMBER_TYPES.SENATE :
    MEMBER_TYPES.HOUSE
  );

  const handleClick = () => {
    if (onClick) {
      onClick(member);
    } else {
      navigateToMemberFromRaw(member, determinedType);
    }
  };

  // Get role text based on member type
  const getRoleText = () => {
    switch (determinedType) {
      case MEMBER_TYPES.PRESIDENT:
        return `${getOrdinalSuffix(member.president_num)} U.S. President`;
      case MEMBER_TYPES.VICE_PRESIDENT:
        return 'U.S. Vice President';
      case MEMBER_TYPES.JUSTICE:
        return 'U.S. Supreme Court Justice';
      case MEMBER_TYPES.SENATE:
        return 'U.S. Senate';
      case MEMBER_TYPES.HOUSE:
      default:
        return 'U.S. House of Representatives';
    }
  };

  // Get location info (or title for justices)
  const getLocationInfo = () => {
    if (determinedType === MEMBER_TYPES.PRESIDENT || determinedType === MEMBER_TYPES.VICE_PRESIDENT) {
      return null;
    }
    if (determinedType === MEMBER_TYPES.JUSTICE) {
      // Show title for justices (e.g., "Chief Justice" or "Associate Justice")
      return title || null;
    }
    if (determinedType === MEMBER_TYPES.SENATE) {
      return stateName;
    }
    return district 
      ? `${getOrdinalSuffix(district)} District ${stateName}`
      : stateName;
  };

  // Get year info (or appointment date for justices)
  const getYearInfo = () => {
    if (determinedType === MEMBER_TYPES.PRESIDENT || determinedType === MEMBER_TYPES.VICE_PRESIDENT) {
      // Show term start year for presidents/VPs
      if (termStart) {
        return new Date(termStart).getFullYear();
      }
      return null;
    }
    if (determinedType === MEMBER_TYPES.JUSTICE) {
      // Show appointment date for justices (e.g., "September 29, 2005")
      return appointmentDate || null;
    }
    return startYear;
  };

  // Compact variant
  if (variant === 'compact') {
    return (
      <Container id={containerId} onClick={handleClick}>
        <div className="member-card-row member-card-row--center">
          {imageSource && (
            <div id='profileImage'>
              <Image id='profileImage-image' src={imageSource} />
            </div>
          )}
          <div className="member-card-info member-card-info--compact">
            <div className="member-card-name member-card-name--compact">
              {fullName}
            </div>
            <div className="member-card-text member-card-text--compact">
              {getRoleText()}
            </div>
          </div>
        </div>
      </Container>
    );
  }

  // Carousel variant
  if (variant === 'carousel') {
    return (
      <Container id='repContainer' onClick={handleClick}>
        <div className="member-card-row">
          {imageSource && (
            <div id='profileImage'>
              <Image id='profileImage-image' src={imageSource} />
            </div>
          )}
          <div className="member-card-info">
            <div className="member-card-name">
              {fullName}
            </div>
            <div className="member-card-text">
              {getRoleText()}
            </div>
            {getLocationInfo() && (
              <div className="member-card-text">
                {getLocationInfo()}
              </div>
            )}
            {getYearInfo() && (
              <div className="member-card-text">
                {getYearInfo()}
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  }

  // Full variant (default)
  return (
    <div id='feedDisplay'>
      <Container id={containerId} onClick={handleClick}>
        <div className="member-card-row">
          {imageSource && (
            <div id='profileImage'>
              <Image id='profileImage-image' src={imageSource} />
            </div>
          )}
          <div className="member-card-info">
            <div className="member-card-name">
              {fullName}
            </div>
            <div className="member-card-text">
              {getRoleText()}
            </div>
            {getLocationInfo() && (
              <div className="member-card-text">
                {getLocationInfo()}
              </div>
            )}
            {getYearInfo() && (
              <div className="member-card-text">
                {getYearInfo()}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default MemberCard;